import { useEffect, useMemo, useState, type CSSProperties, type FormEvent } from "react";
import { CalendarLinks } from "../../components/CalendarLinks";
import { DepartmentFilter, useDepartments } from "../../hooks/useDepartments";
import { useEntitlements } from "../../hooks/useEntitlements";
import { collection, doc, onSnapshot, orderBy, query, updateDoc, where } from "firebase/firestore";
import { editStamp } from "../../lib/editStamp";
import { Link, useLocation } from "wouter";
import { ChevronLeft, ChevronRight, Pencil, Route } from "lucide-react";
import {
  paths,
  jobFlag,
  JOB_FLAG_LABEL,
  ROLE_RANK,
  type Department,
  type Job,
  type RouteDoc,
  type RoutingSettings,
} from "@clevis/shared-types";
import { Button, Card, Input, Label, Select, cn } from "@clevis/shared-ui";
import { db } from "../../lib/firebase";
import { call } from "../../lib/callFunction";
import { textMatches } from "../../lib/filter";
import { useAuth } from "../../hooks/useAuth";
import { useIndustry } from "../../hooks/useIndustry";
import { LiveTechMap } from "../../components/LiveTechMap";
import { MonthView, UpcomingView, WeekView } from "../../components/ScheduleRange";
import { EVERYONE, TechPicker, type TechSelection } from "../../components/TechPicker";

// Dispatch, rebuilt for real-company scale after CSG's data broke v1.
// Three lenses on one day:
//   Jobs  — the flat list, dense and filterable
//   Depts — department queues; the dept manager works their own triage
//   Techs — one lane per tech in route order; two-tap assign
// (+ Timeline keeps the classic hour grid for real-appointment shops.)
// Assigning: tap a job in triage, tap a lane. The optimizer re-times
// routes by itself when jobs move.

type JobRow = Job & { id: string };
interface Member {
  uid: string;
  name: string;
}

const DAY_START_H = 6;
const DAY_END_H = 20;
const HOUR_PX = 48;

function dayBounds(d: Date): { start: number; end: number } {
  const start = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  return { start, end: start + 24 * 60 * 60 * 1000 };
}

type Tab = "jobs" | "depts" | "techs" | "timeline";
type Range = "day" | "week" | "month" | "upcoming";
const NO_TECH = "__none";

export function SchedulePage() {
  const { tenantId, user, role } = useAuth();
  const routingOn = useEntitlements().hasFeature("routing");
  const { vocab } = useIndustry();
  const [day, setDay] = useState(() => new Date());
  const [tab, setTab] = useState<Tab>("techs");
  // Day (dispatch board), week, month, or the upcoming list; remembered on this device.
  const [range, setRangeState] = useState<Range>(() => {
    try {
      const v = localStorage.getItem("clevis.scheduleRange");
      return v === "week" || v === "month" || v === "upcoming" ? v : "day";
    } catch {
      return "day";
    }
  });
  const setRange = (r: Range) => {
    setRangeState(r);
    try {
      localStorage.setItem("clevis.scheduleRange", r);
    } catch {
      /* private mode */
    }
  };
  // Everyone, nobody yet, or a mix of departments and people; remembered on this device.
  const [techSel, setTechSelState] = useState<TechSelection>(() => {
    try {
      const v = JSON.parse(localStorage.getItem("clevis.scheduleTechs") ?? "null") as TechSelection | null;
      return v && Array.isArray(v.uids) ? { uids: v.uids.filter((u) => typeof u === "string"), noTech: !!v.noTech } : EVERYONE;
    } catch {
      return EVERYONE;
    }
  });
  const setTechSel = (v: TechSelection) => {
    setTechSelState(v);
    try {
      localStorage.setItem("clevis.scheduleTechs", JSON.stringify(v));
    } catch {
      /* private mode */
    }
  };
  const [upcomingDays, setUpcomingDays] = useState(14);
  // Week: day columns, tech rows × day columns, or day rows × tech columns; remembered on this device.
  const [weekLayout, setWeekLayoutState] = useState<"calendar" | "techs" | "days">(() => {
    try {
      const v = localStorage.getItem("clevis.weekLayout");
      return v === "techs" || v === "days" ? v : "calendar";
    } catch {
      return "calendar";
    }
  });
  const setWeekLayout = (l: "calendar" | "techs" | "days") => {
    setWeekLayoutState(l);
    try {
      localStorage.setItem("clevis.weekLayout", l);
    } catch {
      /* private mode */
    }
  };
  const [rangeJobs, setJobs] = useState<JobRow[]>([]);
  const [allDateless, setDateless] = useState<JobRow[]>([]);
  const [allMembers, setMembers] = useState<Member[]>([]);
  // The department this person schedules (remembered): its jobs and its people's lanes.
  const dept = useDepartments();
  const deptJobs = useMemo(() => rangeJobs.filter((j) => dept.jobMatches(j)), [rangeJobs, dept.current, dept.enabled]); // eslint-disable-line react-hooks/exhaustive-deps
  const dateless = useMemo(() => allDateless.filter((j) => dept.jobMatches(j)), [allDateless, dept.current, dept.enabled]); // eslint-disable-line react-hooks/exhaustive-deps
  const deptMembers = useMemo(() => allMembers.filter((m) => dept.personMatches(m.uid)), [allMembers, dept.current, dept.enabled]); // eslint-disable-line react-hooks/exhaustive-deps
  // Techs picked: only their jobs and their lanes (people who've left the team drop out of the pick).
  const picked = useMemo(() => new Set(techSel.uids.filter((u) => allMembers.some((m) => m.uid === u))), [techSel.uids, allMembers]);
  const tech = techSel.noTech ? NO_TECH : picked.size ? "some" : "";
  const jobs = useMemo(
    () => (techSel.noTech ? deptJobs.filter((j) => j.assignedUids.length === 0) : picked.size ? deptJobs.filter((j) => j.assignedUids.some((u) => picked.has(u))) : deptJobs),
    [deptJobs, techSel.noTech, picked],
  );
  const members = useMemo(() => (picked.size ? deptMembers.filter((m) => picked.has(m.uid)) : deptMembers), [deptMembers, picked]);
  const [departments, setDepartments] = useState<Array<Department & { id: string }>>([]);
  const [activeDept, setActiveDept] = useState<string | null>(null);
  const [selected, setSelected] = useState<JobRow | null>(null); // quick-edit panel
  const [pickId, setPickId] = useState<string | null>(null); // selected for assignment
  const [settings, setSettings] = useState<RoutingSettings | null>(null);
  const [routes, setRoutes] = useState<Record<string, RouteDoc>>({});
  const [optimizing, setOptimizing] = useState<string | null>(null);
  const [message, setMessage] = useState("");

  const { start, end } = dayBounds(day);
  const weekStart = new Date(day.getFullYear(), day.getMonth(), day.getDate() - day.getDay()).getTime();
  const monthStart = new Date(day.getFullYear(), day.getMonth(), 1).getTime();
  const gridStart = new Date(day.getFullYear(), day.getMonth(), 1 - new Date(monthStart).getDay());
  // What to load: the day, the week, or the whole month grid (6 weeks).
  let [qStart, qEnd] =
    range === "day"
      ? [start, end]
      : range === "week"
        ? [weekStart, new Date(new Date(weekStart).getFullYear(), new Date(weekStart).getMonth(), new Date(weekStart).getDate() + 7).getTime()]
        : [gridStart.getTime(), new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + 42).getTime()];
  const todayStart = dayBounds(new Date()).start;
  if (range === "upcoming") [qStart, qEnd] = [todayStart, new Date(new Date(todayStart).getFullYear(), new Date(todayStart).getMonth(), new Date(todayStart).getDate() + upcomingDays).getTime()];
  const isManager = role !== null && ROLE_RANK[role] >= 2;
  const isAdmin = role !== null && ROLE_RANK[role] >= 3;
  // A Field tech given Schedule (Roles & access) looks, never dispatches: no
  // assigning, no edit panel, no routing others; a chip opens the job.
  const canDispatch = role !== "tech";
  const [, navigate] = useLocation();
  const openOrEdit = (j: JobRow) => (canDispatch ? setSelected(j) : navigate(`/jobs/${j.id}`));
  const tz = settings?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const dateStr = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(start + 12 * 3600_000));

  useEffect(() => {
    if (!tenantId) return;
    const subs = [
      onSnapshot(doc(db, paths.tenantRoutingSettings(tenantId)), (snap) =>
        setSettings(snap.exists() ? (snap.data() as RoutingSettings) : null),
      ),
      onSnapshot(query(collection(db, paths.tenantRoutes(tenantId)), where("date", "==", dateStr)), (snap) => {
        const next: Record<string, RouteDoc> = {};
        snap.docs.forEach((d) => {
          const r = d.data() as RouteDoc;
          next[r.techUid] = r;
        });
        setRoutes(next);
      }),
      onSnapshot(collection(db, paths.tenantDepartments(tenantId)), (snap) =>
        // Department queues (groups are people filters, not queues).
        setDepartments(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Department) })).filter((d) => d.kind !== "group")),
      ),
    ];
    return () => subs.forEach((u) => u());
  }, [tenantId, dateStr]);

  useEffect(() => {
    if (!tenantId) return;
    const subs = [
      onSnapshot(
        query(
          collection(db, paths.tenantJobs(tenantId)),
          where("startAt", ">=", qStart),
          where("startAt", "<", qEnd),
          orderBy("startAt"),
        ),
        (snap) => setJobs(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Job) }))),
      ),
      onSnapshot(query(collection(db, paths.tenantJobs(tenantId)), where("startAt", "==", null)), (snap) =>
        setDateless(
          snap.docs.map((d) => ({ id: d.id, ...(d.data() as Job) })).filter((j) => j.status === "scheduled"),
        ),
      ),
      onSnapshot(collection(db, paths.tenantMembers(tenantId)), (snap) =>
        setMembers(
          snap.docs
            .filter((d) => d.get("disabled") !== true)
            .map((d) => ({ uid: d.id, name: (d.get("displayName") as string) || (d.get("email") as string) })),
        ),
      ),
    ];
    return () => subs.forEach((u) => u());
  }, [tenantId, qStart, qEnd]);

  const unassigned = useMemo(
    () => [...deptJobs.filter((j) => j.assignedUids.length === 0 && j.status !== "canceled"), ...dateless],
    [deptJobs, dateless],
  );
  // Undated jobs belong to nobody yet, so they show unless one tech is picked.
  const shownDateless = tech === "" || tech === NO_TECH ? dateless : [];
  const activeTechs = useMemo(() => {
    const counts = new Map<string, number>();
    for (const j of jobs) for (const uid of j.assignedUids) counts.set(uid, (counts.get(uid) ?? 0) + 1);
    return [...members].sort(
      (a, b) => (counts.get(b.uid) ?? 0) - (counts.get(a.uid) ?? 0) || a.name.localeCompare(b.name),
    );
  }, [jobs, members]);

  const memberName = (uid: string) => allMembers.find((m) => m.uid === uid)?.name ?? "?";
  const deptName = (id: string | null) => departments.find((d) => d.id === id)?.name ?? "";
  const pickedJob = useMemo(
    () => unassigned.find((j) => j.id === pickId) ?? jobs.find((j) => j.id === pickId) ?? null,
    [unassigned, jobs, pickId],
  );

  async function patchJob(jobId: string, patch: Partial<Job>) {
    if (!tenantId) return;
    await updateDoc(doc(db, paths.tenantJob(tenantId, jobId)), { ...patch, ...editStamp(user) });
    setSelected(null);
  }

  // The two-tap assign: a picked job lands on the tapped tech. Dateless jobs
  // get a 9am slot so they show on boards; the optimizer re-times from there.
  async function assignPicked(techUid: string) {
    const job = pickedJob;
    if (!job) return;
    const patch: Partial<Job> = { assignedUids: [techUid] };
    if (job.startAt === null) {
      patch.startAt = start + 9 * 3600_000;
      patch.endAt = start + 11 * 3600_000;
    }
    await patchJob(job.id, patch);
    setPickId(null);
    setMessage(`${job.title.slice(0, 40)} → ${memberName(techUid)}`);
  }

  async function optimize(techUid: string) {
    if (!tenantId) return;
    setOptimizing(techUid);
    setMessage("");
    try {
      const isToday = dayBounds(new Date()).start === start;
      await call("optimizeTechRoute", { tenantId, date: dateStr, techUid, startFrom: isToday ? "tech" : "home" });
    } catch (err) {
      setMessage((err as Error).message);
    } finally {
      setOptimizing(null);
    }
  }

  const fmtDayLabel = day.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
  const isToday = dayBounds(new Date()).start === start;

  function shiftDay(delta: number) {
    const d = new Date(day);
    if (range === "month") d.setMonth(d.getMonth() + delta, 1);
    else d.setDate(d.getDate() + delta * (range === "week" ? 7 : 1));
    setDay(d);
  }
  const rangeLabel =
    range === "upcoming"
      ? `Next ${upcomingDays} days`
      : range === "day"
      ? fmtDayLabel
      : range === "week"
        ? `${new Date(weekStart).toLocaleDateString(undefined, { month: "short", day: "numeric" })} – ${new Date(weekStart + 6 * 86_400_000).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`
        : new Date(monthStart).toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const openDay = (d: number) => {
    setDay(new Date(d));
    setRange("day");
  };

  const TABS: Array<{ id: Tab; label: string }> = [
    { id: "jobs", label: vocab.jobs },
    ...(departments.length > 0 ? [{ id: "depts" as Tab, label: "Depts" }] : []),
    { id: "techs", label: vocab.techs },
    { id: "timeline", label: "Timeline" },
  ];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-lg font-bold">Schedule</h1>
          <div className="flex rounded-lg border border-steel/30 p-0.5 text-xs font-semibold" role="tablist" aria-label="Range">
            {(["day", "week", "month", "upcoming"] as const).map((r) => (
              <button key={r} className={cn("rounded-md px-3 py-1 capitalize", range === r ? "bg-brandorange text-white" : "text-steel hover:text-ink")} onClick={() => setRange(r)}>
                {r}
              </button>
            ))}
          </div>
          {range === "week" ? (
            <div className="flex rounded-lg border border-steel/30 p-0.5 text-xs">
              {(["calendar", "techs", "days"] as const).map((l) => (
                <button key={l} className={cn("rounded-md px-3 py-1", weekLayout === l ? "bg-ink text-white" : "text-steel")} onClick={() => setWeekLayout(l)} title={l === "techs" ? `${vocab.techs} down the side, days across` : l === "days" ? `Days down the side, ${vocab.techs.toLowerCase()} across` : "A column per day"}>
                  {l === "calendar" ? "By day" : l === "techs" ? `${vocab.techs} × days` : `Days × ${vocab.techs.toLowerCase()}`}
                </button>
              ))}
            </div>
          ) : null}
          <div className={cn("flex rounded-lg border border-steel/30 p-0.5 text-xs", range !== "day" && "hidden")}>
            {TABS.map((t) => (
              <button
                key={t.id}
                className={cn("rounded-md px-3 py-1", tab === t.id ? "bg-ink text-white" : "text-steel")}
                onClick={() => setTab(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <DepartmentFilter dept={dept} className="w-auto py-1 text-xs" />
          <TechPicker className="mr-2" members={deptMembers} departments={dept.departments} value={techSel} onChange={setTechSel} techWord={vocab.techs.toLowerCase()} />
          <Button
            variant="ghost"
            className={cn("px-2.5 py-1 text-xs", isToday && range === "day" && "text-brandorange")}
            onClick={() => {
              setDay(new Date());
              if (range === "upcoming") setRange("day");
            }}
          >
            Today
          </Button>
          {range !== "upcoming" ? (
            <Button variant="ghost" className="px-2 py-1" onClick={() => shiftDay(-1)} aria-label="Previous">
              <ChevronLeft size={16} />
            </Button>
          ) : null}
          <span className={cn("px-1 text-sm font-medium", range === "day" && isToday ? "text-brandorange" : "text-ink")}>{rangeLabel}</span>
          {range !== "upcoming" ? (
            <Button variant="ghost" className="px-2 py-1" onClick={() => shiftDay(1)} aria-label="Next">
              <ChevronRight size={16} />
            </Button>
          ) : null}
        </div>
      </div>

      {pickedJob ? (
        <div className="flex items-center justify-between rounded-lg border border-brandorange bg-brandorange/10 px-3 py-1.5 text-xs">
          <span className="min-w-0 truncate text-brandorange-dark">
            Assigning: <strong>{pickedJob.title}</strong> — tap a tech
          </span>
          <button className="shrink-0 text-steel hover:text-ink" onClick={() => setPickId(null)}>
            cancel
          </button>
        </div>
      ) : null}
      {message ? <p className="text-xs text-ink">{message}</p> : null}

      {(range === "week" || range === "month") && shownDateless.length ? (
        <button className="w-full rounded-lg bg-brandorange/10 px-3 py-1.5 text-left text-xs text-ink hover:bg-brandorange/20" onClick={() => setRange("upcoming")}>
          {shownDateless.length} job{shownDateless.length === 1 ? " has" : "s have"} no date yet, so {shownDateless.length === 1 ? "it isn't" : "they aren't"} on the calendar. <strong>See them →</strong>
        </button>
      ) : null}
      {range === "upcoming" ? (
        <UpcomingView
          fromDay={todayStart}
          days={upcomingDays}
          jobs={jobs.filter((j) => j.status !== "canceled")}
          dateless={shownDateless}
          memberName={memberName}
          onMore={() => setUpcomingDays((n) => Math.min(n + 14, 90))}
          onOpenDay={openDay}
        />
      ) : null}
      {range === "week" ? (
        <WeekView weekStart={weekStart} jobs={jobs.filter((j) => j.status !== "canceled")} members={members} memberName={memberName} layout={weekLayout} onOpenDay={openDay} />
      ) : null}
      {range === "month" ? <MonthView monthStart={monthStart} jobs={jobs.filter((j) => j.status !== "canceled")} memberName={memberName} onOpenDay={openDay} /> : null}

      {range === "day" && tab === "jobs" ? (
        <JobsTab jobs={jobs} dateless={shownDateless} deptName={deptName} memberName={memberName} settings={settings} />
      ) : null}

      {range === "day" && tab === "depts" ? (
        <DeptsTab
          departments={departments}
          activeDept={activeDept}
          setActiveDept={setActiveDept}
          unassigned={unassigned}
          jobs={jobs}
          pickId={pickId}
          setPickId={setPickId}
          setSelected={openOrEdit}
          techs={activeTechs}
          routes={routes}
          settings={settings}
          optimizing={optimizing}
          optimize={optimize}
          assignPicked={canDispatch ? assignPicked : async () => undefined}
          canRoute={(uid) => canDispatch && routingOn && settings?.homeLat != null && (isManager || uid === user?.uid)}
        />
      ) : null}

      {range === "day" && tab === "techs" ? (
        <div className={cn("grid gap-3", canDispatch && "md:grid-cols-[280px_minmax(0,1fr)]")}>
          {canDispatch ? (
            <TriageRail
              title="Needs a tech"
              items={unassigned}
              pickId={pickId}
              setPickId={setPickId}
              setSelected={openOrEdit}
            />
          ) : null}
          <TechLanes
            techs={activeTechs}
            jobs={jobs}
            routes={routes}
            settings={settings}
            optimizing={optimizing}
            optimize={optimize}
            picked={!!pickedJob}
            assignPicked={canDispatch ? assignPicked : async () => undefined}
            setSelected={openOrEdit}
            canRoute={(uid) => canDispatch && routingOn && settings?.homeLat != null && (isManager || uid === user?.uid)}
          />
        </div>
      ) : null}

      {range === "day" && tab === "timeline" ? (
        <TimelineGrid
          jobs={jobs}
          techs={activeTechs.filter((t) => jobs.some((j) => j.assignedUids.includes(t.uid)))}
          start={start}
          settings={settings}
          setSelected={openOrEdit}
        />
      ) : null}

      {settings?.homeLat != null ? (
        routingOn && <LiveTechMap tenantId={tenantId ?? ""} memberName={memberName} home={{ lat: settings.homeLat, lng: settings.homeLng! }} />
      ) : null}

      {isAdmin ? <RoutingSetupCard tenantId={tenantId ?? ""} settings={settings} /> : null}

      {tenantId ? <CalendarLinks tenantId={tenantId} manager={isManager} /> : null}

      {selected ? (
        <JobQuickPanel
          job={selected}
          members={members}
          memberName={memberName}
          onClose={() => setSelected(null)}
          onSave={(patch) => void patchJob(selected.id, patch)}
        />
      ) : null}
    </div>
  );
}

// ---------- Jobs tab: the flat, dense, filterable day list ----------

function JobsTab({
  jobs,
  dateless,
  deptName,
  memberName,
  settings,
}: {
  jobs: JobRow[];
  dateless: JobRow[];
  deptName: (id: string | null) => string;
  memberName: (uid: string) => string;
  settings: RoutingSettings | null;
}) {
  const [search, setSearch] = useState("");
  const all = useMemo(() => [...jobs, ...dateless], [jobs, dateless]);
  const visible = useMemo(
    () =>
      all.filter((j) =>
        textMatches(search, j.title, j.address, j.note, j.status, deptName(j.departmentId), ...j.assignedUids.map(memberName)),
      ),
    [all, search, deptName, memberName],
  );
  return (
    <>
      <Input placeholder="Filter the day — title, tech, dept, address…" value={search} onChange={(e) => setSearch(e.target.value)} />
      <Card className="p-0">
        {visible.map((j) => {
          const flag = jobFlag(j, settings?.onTimeToleranceMin ?? 15);
          const alarmed = flag === "late" || flag === "atRisk";
          return (
            <Link
              key={j.id}
              href={`/jobs/${j.id}`}
              className="flex items-center justify-between gap-2 border-b border-steel/10 px-4 py-1.5 text-sm last:border-0 hover:bg-cream/60"
            >
              <span className="min-w-0">
                <span className="block truncate text-ink">{j.title}</span>
                <span className="block truncate text-xs text-steel">
                  {[
                    j.startAt ? new Date(j.startAt).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }) : "no time",
                    deptName(j.departmentId),
                    j.assignedUids.length > 0 ? j.assignedUids.map(memberName).join(", ") : "unassigned",
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </span>
              <span
                className={cn(
                  "ml-2 shrink-0 rounded-full px-2 py-0.5 text-xs",
                  alarmed ? "bg-danger/10 text-danger" : j.status === "done" ? "bg-success/15 text-success" : "bg-steel/15 text-ink",
                )}
              >
                {alarmed ? JOB_FLAG_LABEL[flag] : j.status.replace("_", " ")}
              </span>
            </Link>
          );
        })}
        {visible.length === 0 ? <p className="px-4 py-8 text-center text-sm text-steel">Nothing matches.</p> : null}
      </Card>
    </>
  );
}

// ---------- Triage rail: dense, selectable, the assign source ----------

function TriageRail({
  title,
  items,
  pickId,
  setPickId,
  setSelected,
}: {
  title: string;
  items: JobRow[];
  pickId: string | null;
  setPickId: (id: string | null) => void;
  setSelected: (j: JobRow) => void;
}) {
  const [search, setSearch] = useState("");
  const visible = useMemo(() => items.filter((j) => textMatches(search, j.title, j.address, j.note)), [items, search]);
  return (
    <Card className="self-start p-0">
      <div className="flex items-center justify-between px-3 pt-2">
        <span className="text-xs font-semibold text-brandorange-dark">
          {title} ({items.length})
        </span>
      </div>
      <div className="px-3 py-2">
        <Input className="py-1 text-xs" placeholder="Filter…" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>
      <div className="max-h-[480px] overflow-y-auto">
        {visible.map((j) => (
          <div
            key={j.id}
            className={cn(
              "flex w-full items-center justify-between gap-1 border-t border-steel/10 px-3 py-1.5 text-left text-xs",
              pickId === j.id ? "bg-brandorange/10" : "hover:bg-cream/60",
            )}
          >
            <button className="min-w-0 flex-1 text-left" onClick={() => setPickId(pickId === j.id ? null : j.id)}>
              <span className={cn("block truncate", pickId === j.id ? "font-semibold text-brandorange-dark" : "text-ink")}>
                {j.title}
              </span>
              <span className="block truncate text-steel">
                {j.startAt ? new Date(j.startAt).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }) : "no time"}
                {j.address ? ` · ${j.address}` : ""}
              </span>
            </button>
            <button className="shrink-0 p-1 text-steel hover:text-ink" title="Edit time/tech" onClick={() => setSelected(j)}>
              <Pencil size={12} />
            </button>
          </div>
        ))}
        {visible.length === 0 ? (
          <p className="border-t border-steel/10 px-3 py-6 text-center text-xs text-steel">
            {search ? "No matches." : "Everything has a tech. 🎉"}
          </p>
        ) : null}
      </div>
    </Card>
  );
}

// ---------- Tech lanes: one row per tech, chips in route order ----------

function TechLanes({
  techs,
  jobs,
  routes,
  settings,
  optimizing,
  optimize,
  picked,
  assignPicked,
  setSelected,
  canRoute,
}: {
  techs: Member[];
  jobs: JobRow[];
  routes: Record<string, RouteDoc>;
  settings: RoutingSettings | null;
  optimizing: string | null;
  optimize: (uid: string) => void;
  picked: boolean;
  assignPicked: (uid: string) => void;
  setSelected: (j: JobRow) => void;
  canRoute: (uid: string) => boolean;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      {techs.map((tech) => {
        const mine = jobs
          .filter((j) => j.assignedUids.includes(tech.uid))
          .sort((a, b) => (a.sequence ?? 1 << 30) - (b.sequence ?? 1 << 30) || (a.startAt ?? 0) - (b.startAt ?? 0));
        const route = routes[tech.uid];
        return (
          <Card
            key={tech.uid}
            className={cn("p-2.5", picked && "cursor-pointer border-dashed border-brandorange/70 hover:bg-brandorange/5")}
            onClick={picked ? () => assignPicked(tech.uid) : undefined}
          >
            <div className="flex items-baseline justify-between gap-2">
              <span className="truncate text-sm font-semibold text-ink">
                {tech.name}
                {picked ? <span className="ml-2 text-xs font-normal text-brandorange-dark">tap to assign</span> : null}
              </span>
              <span className="flex shrink-0 items-center gap-1 text-[11px] text-steel">
                {mine.length > 0
                  ? `${mine.length} job${mine.length === 1 ? "" : "s"}${
                      route && route.jobCount > 0
                        ? ` · ${Math.round(route.distanceMeters / 1609)} mi · ${Math.floor(route.durationSeconds / 3600)}h${Math.round((route.durationSeconds % 3600) / 60)}m`
                        : ""
                    }`
                  : "free all day"}
                {canRoute(tech.uid) && mine.length > 0 ? (
                  <button
                    className="rounded-md p-0.5 text-steel hover:bg-cream hover:text-brandorange disabled:opacity-40"
                    title="Optimize this route"
                    disabled={optimizing === tech.uid}
                    onClick={(e) => {
                      e.stopPropagation();
                      optimize(tech.uid);
                    }}
                  >
                    <Route size={13} className={optimizing === tech.uid ? "animate-pulse" : undefined} />
                  </button>
                ) : null}
              </span>
            </div>
            {mine.length > 0 ? (
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {mine.map((j) => {
                  const flag = jobFlag(j, settings?.onTimeToleranceMin ?? 15);
                  const alarmed = flag === "late" || flag === "atRisk";
                  return (
                    <button
                      key={j.id}
                      className={cn(
                        "max-w-full truncate rounded-md border px-2 py-1 text-[11px]",
                        j.status === "done"
                          ? "border-success/40 bg-success/10 text-success"
                          : j.status === "in_progress"
                            ? "border-brandorange/50 bg-brandorange/10 text-brandorange-dark"
                            : "border-steel/25 bg-cream text-ink",
                        alarmed && "ring-1 ring-danger",
                      )}
                      title={`${j.title}${alarmed ? ` — ${JOB_FLAG_LABEL[flag]}` : ""}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelected(j);
                      }}
                    >
                      {j.sequence != null ? `${j.sequence} · ` : ""}
                      {j.title.slice(0, 34)}
                      {j.plannedEtaAt
                        ? ` · ${new Date(j.plannedEtaAt).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`
                        : ""}
                    </button>
                  );
                })}
              </div>
            ) : null}
          </Card>
        );
      })}
    </div>
  );
}

// ---------- Depts tab: queues first, drill into one ----------

function DeptsTab(props: {
  departments: Array<Department & { id: string }>;
  activeDept: string | null;
  setActiveDept: (id: string | null) => void;
  unassigned: JobRow[];
  jobs: JobRow[];
  pickId: string | null;
  setPickId: (id: string | null) => void;
  setSelected: (j: JobRow) => void;
  techs: Member[];
  routes: Record<string, RouteDoc>;
  settings: RoutingSettings | null;
  optimizing: string | null;
  optimize: (uid: string) => void;
  assignPicked: (uid: string) => void;
  canRoute: (uid: string) => boolean;
}) {
  const { departments, activeDept, setActiveDept, unassigned } = props;
  const cards = [
    ...departments.map((d) => ({ id: d.id as string | null, name: d.name })),
    { id: null, name: "Unrouted" },
  ];
  const countFor = (id: string | null) => unassigned.filter((j) => (j.departmentId ?? null) === id).length;
  const deptUnassigned = unassigned.filter((j) => (j.departmentId ?? null) === activeDept);

  return (
    <>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {cards.map((c) => {
          const n = countFor(c.id);
          const active = activeDept === c.id;
          return (
            <button
              key={c.id ?? "none"}
              className={cn(
                "rounded-xl border bg-white px-3 py-2 text-left",
                active ? "border-brandorange" : "border-steel/25 hover:border-brandorange/60",
              )}
              onClick={() => setActiveDept(c.id)}
            >
              <div className="truncate text-sm font-semibold text-ink">{c.name}</div>
              <div className="mt-1 text-[11px]">
                {n > 0 ? (
                  <span className="rounded-full bg-brandorange/15 px-2 py-0.5 text-brandorange-dark">{n} unassigned</span>
                ) : (
                  <span className="text-steel">all assigned</span>
                )}
              </div>
            </button>
          );
        })}
      </div>
      {activeDept !== undefined && (activeDept !== null || countFor(null) > 0 || activeDept === null) ? (
        <div className="grid gap-3 md:grid-cols-[280px_minmax(0,1fr)]">
          <TriageRail
            title={cards.find((c) => c.id === activeDept)?.name ?? "Queue"}
            items={deptUnassigned}
            pickId={props.pickId}
            setPickId={props.setPickId}
            setSelected={props.setSelected}
          />
          <TechLanes
            techs={props.techs}
            jobs={props.jobs}
            routes={props.routes}
            settings={props.settings}
            optimizing={props.optimizing}
            optimize={props.optimize}
            picked={!!props.pickId}
            assignPicked={props.assignPicked}
            setSelected={props.setSelected}
            canRoute={props.canRoute}
          />
        </div>
      ) : null}
    </>
  );
}

// ---------- Timeline: the classic hour grid, for real-appointment days ----------

function TimelineGrid({
  jobs,
  techs,
  start,
  settings,
  setSelected,
}: {
  jobs: JobRow[];
  techs: Member[];
  start: number;
  settings: RoutingSettings | null;
  setSelected: (j: JobRow) => void;
}) {
  const hours = Array.from({ length: DAY_END_H - DAY_START_H }, (_, i) => DAY_START_H + i);
  const statusStyle: Record<string, string> = {
    scheduled: "bg-steel/90 border-steel",
    in_progress: "bg-brandorange border-brandorange-dark",
    done: "bg-success/80 border-success",
    canceled: "bg-steel/30 border-steel/40 line-through",
  };
  function blockStyle(job: JobRow): CSSProperties {
    const s = job.startAt ?? start + DAY_START_H * 3600000;
    const e = job.endAt ?? s + 3600000;
    const startH = Math.max(DAY_START_H, (s - start) / 3600000);
    const endH = Math.min(DAY_END_H, Math.max(startH + 0.5, (e - start) / 3600000));
    return { top: (startH - DAY_START_H) * HOUR_PX, height: (endH - startH) * HOUR_PX - 2 };
  }
  return (
    <Card className="overflow-x-auto p-0">
      {techs.length === 0 ? (
        <div className="px-4 py-10 text-center text-sm text-steel">Nobody has timed work this day.</div>
      ) : (
        <div className="flex min-w-fit">
          <div className="w-14 shrink-0 border-r border-steel/15">
            <div className="h-9 border-b border-steel/15" />
            {hours.map((h) => (
              <div key={h} className="relative border-b border-steel/10 text-right" style={{ height: HOUR_PX }}>
                <span className="pr-1 text-[10px] text-steel">
                  {h % 12 === 0 ? 12 : h % 12}
                  {h < 12 ? "a" : "p"}
                </span>
              </div>
            ))}
          </div>
          {techs.map((tech) => (
            <div key={tech.uid} className="min-w-[160px] flex-1 border-r border-steel/10 last:border-0">
              <div className="flex h-9 items-center justify-center border-b border-steel/15 px-2 text-xs font-semibold text-ink">
                {tech.name}
              </div>
              <div className="relative" style={{ height: (DAY_END_H - DAY_START_H) * HOUR_PX }}>
                {hours.map((h) => (
                  <div
                    key={h}
                    className="absolute inset-x-0 border-b border-steel/10"
                    style={{ top: (h - DAY_START_H) * HOUR_PX, height: HOUR_PX }}
                  />
                ))}
                {jobs
                  .filter((j) => j.assignedUids.includes(tech.uid))
                  .map((j) => {
                    const flag = jobFlag(j, settings?.onTimeToleranceMin ?? 15);
                    const alarmed = flag === "late" || flag === "atRisk";
                    return (
                      <button
                        key={j.id}
                        className={cn(
                          "absolute inset-x-1 overflow-hidden rounded-md border px-1.5 py-0.5 text-left text-[11px] leading-tight text-white",
                          statusStyle[j.status],
                          flag === "late" && "ring-2 ring-danger",
                          flag === "atRisk" && "ring-2 ring-brandorange",
                        )}
                        style={blockStyle(j)}
                        onClick={() => setSelected(j)}
                        title={alarmed ? `${j.title} — ${JOB_FLAG_LABEL[flag]}` : j.title}
                      >
                        <span className="font-semibold">{j.title}</span>
                        <span className="block truncate opacity-80">
                          {j.plannedEtaAt
                            ? `ETA ${new Date(j.plannedEtaAt).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`
                            : j.address || ""}
                        </span>
                      </button>
                    );
                  })}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function RoutingSetupCard({ tenantId, settings }: { tenantId: string; settings: RoutingSettings | null }) {
  const configured = settings?.homeLat != null;
  const [open, setOpen] = useState(!configured);
  const [address, setAddress] = useState(settings?.homeAddress ?? "");
  const [tolerance, setTolerance] = useState(String(settings?.onTimeToleranceMin ?? 15));
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setNote("");
    try {
      await call("setRoutingSettings", {
        tenantId,
        homeAddress: address,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        onTimeToleranceMin: parseInt(tolerance, 10) || 15,
      });
      setNote("Saved — routes can start and end here now.");
      setOpen(false);
    } catch (err) {
      setNote((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <p className="text-[11px] text-steel">
        Home base: {settings?.homeAddress || "—"} · on-time tolerance {settings?.onTimeToleranceMin ?? 15} min ·{" "}
        <button className="underline hover:text-ink" onClick={() => setOpen(true)}>
          edit
        </button>
        {note ? ` · ${note}` : ""}
      </p>
    );
  }
  return (
    <Card className={cn(!configured && "border-brandorange/50")}>
      <div className="mb-1 text-xs font-semibold text-ink">
        {configured ? "Routing settings" : "Turn on route optimization"}
      </div>
      {!configured ? (
        <p className="mb-2 text-xs text-steel">
          Set your company home base — routes start and end here, and the optimizer orders each tech's
          day around it.
        </p>
      ) : null}
      <form onSubmit={save} className="flex flex-wrap items-end gap-2">
        <div className="min-w-56 flex-1">
          <Label>Home base address</Label>
          <Input required value={address} onChange={(e) => setAddress(e.target.value)} placeholder="123 Shop St, Las Vegas, NV" />
        </div>
        <div>
          <Label>On-time tolerance (min)</Label>
          <Input type="number" min="0" max="240" className="w-24" value={tolerance} onChange={(e) => setTolerance(e.target.value)} />
        </div>
        <Button type="submit" busy={busy}>
          Save
        </Button>
        {configured ? (
          <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
            Cancel
          </Button>
        ) : null}
      </form>
      {note ? <p className="mt-1 text-xs text-steel">{note}</p> : null}
    </Card>
  );
}

function JobQuickPanel({
  job,
  members,
  memberName,
  onClose,
  onSave,
}: {
  job: JobRow;
  members: Member[];
  memberName: (uid: string) => string;
  onClose: () => void;
  onSave: (patch: Partial<Job>) => void;
}) {
  const initialDate = job.startAt ? new Date(job.startAt) : new Date();
  const [date, setDate] = useState(initialDate.toISOString().slice(0, 10));
  const [startT, setStartT] = useState(job.startAt ? new Date(job.startAt).toTimeString().slice(0, 5) : "08:00");
  const [endT, setEndT] = useState(job.endAt ? new Date(job.endAt).toTimeString().slice(0, 5) : "10:00");
  const [techUid, setTechUid] = useState(job.assignedUids[0] ?? "");

  function save() {
    const startAt = new Date(`${date}T${startT}`).getTime();
    const endAt = new Date(`${date}T${endT}`).getTime();
    onSave({
      startAt,
      endAt: endAt > startAt ? endAt : startAt + 3600000,
      assignedUids: techUid ? [techUid] : [],
    });
  }

  return (
    <div className="fixed inset-0 z-30 flex items-end justify-center bg-ink/30 p-4 md:items-center" onClick={onClose}>
      <div className="w-full max-w-sm rounded-xl bg-white p-4 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <h2 className="mb-1 text-sm font-bold text-ink">{job.title}</h2>
        <p className="mb-3 text-xs text-steel">
          {job.assignedUids.length > 0 ? `Currently: ${job.assignedUids.map(memberName).join(", ")}` : "Unassigned"}
        </p>
        <div className="space-y-3">
          <div>
            <Label>Technician</Label>
            <Select value={techUid} onChange={(e) => setTechUid(e.target.value)}>
              <option value="">— unassigned —</option>
              {members.map((m) => (
                <option key={m.uid} value={m.uid}>
                  {m.name}
                </option>
              ))}
            </Select>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label>Date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div>
              <Label>Start</Label>
              <Input type="time" value={startT} onChange={(e) => setStartT(e.target.value)} />
            </div>
            <div>
              <Label>End</Label>
              <Input type="time" value={endT} onChange={(e) => setEndT(e.target.value)} />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={save}>Save</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
